/**
 * Two-step flow to link a worklist patient to another subscribed trial.
 */

import { Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { closeModal, Modal, openModal, Select } from "risa-oasis-ui_v2";
import { Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import type {
  EligibilitySelectOption,
  EligibilityTrialOption,
} from "../../data-model/eligibilityOptions";
import { getTrialTitle } from "../../data-model/eligibilityOptions";
import { ModalId } from "../../enums/modalId";

const DIALOG_ID = ModalId.ADD_MONITORING_TRIAL_MODAL;

/** Context needed to link one patient from the worklist. */
export interface AddMonitoringTrialContext {
  mrn: string;
  linkedTrialIds: string[];
}

/** Props for the add-monitoring-trial dialog. */
export interface AddMonitoringTrialModalProps {
  isOpen: boolean;
  context: AddMonitoringTrialContext | null;
  sourceTrialId: string;
  trials: EligibilityTrialOption[];
  eligibilityTierOptions: EligibilitySelectOption[];
  eligibilityStatusOptions: EligibilitySelectOption[];
  patientStatusOptions: EligibilitySelectOption[];
  isSaving: boolean;
  onClose: () => void;
  onSave: (payload: {
    targetTrialId: string;
    eligibilityStatus: string;
    patientStatus: string;
  }) => Promise<void>;
}

/**
 * Resolve a short phase or site label for the trial picker list.
 *
 * @param trial - Subscribed trial option from the options API
 * @returns Badge label when site metadata is present
 */
function getTrialBadgeLabel(trial: EligibilityTrialOption): string | null {
  return trial.site_status?.trim() || null;
}

/**
 * Filter subscribed trials by NCT id, protocol id, or display label.
 *
 * @param trials - Facility trial options
 * @param query - Case-insensitive search text
 * @returns Trials matching the query
 */
function filterTrials(
  trials: EligibilityTrialOption[],
  query: string,
): EligibilityTrialOption[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return trials;
  return trials.filter((trial) => {
    const haystack = [
      trial.value,
      trial.label,
      trial.nct_id,
      trial.sponsor_protocol_id,
      trial.sponsor,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(normalized);
  });
}

/**
 * Compact trial summary used in the picker and confirmation step.
 *
 * @param props - Trial metadata to display
 * @returns Trial name, NCT id, and optional badge
 */
function TrialSummaryCard({ trial }: { trial: EligibilityTrialOption }) {
  const title = getTrialTitle(trial);
  const badge = getTrialBadgeLabel(trial);

  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-primaryGray-14 bg-white px-4 py-3">
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-primaryGray-1">
          {title}
        </div>
        <div className="text-x-tiny text-primaryGray-4">
          {trial.nct_id || "NCT ID unavailable"}
        </div>
      </div>
      {badge ? (
        <Tags
          label={badge}
          backgroundColor="#FFEDD5"
          textColor="#C2410C"
          className="shrink-0"
        />
      ) : null}
    </div>
  );
}

/**
 * Link a patient on the current trial to another subscribed trial.
 *
 * @param props - {@link AddMonitoringTrialModalProps}
 * @returns Add clinical trial / patient details modal
 */
export function AddMonitoringTrialModal({
  isOpen,
  context,
  sourceTrialId,
  trials,
  eligibilityTierOptions,
  eligibilityStatusOptions,
  patientStatusOptions,
  isSaving,
  onClose,
  onSave,
}: AddMonitoringTrialModalProps) {
  const isModalOpenRef = useRef(false);
  const [step, setStep] = useState<"pick-trial" | "patient-details">("pick-trial");
  const [searchText, setSearchText] = useState("");
  const [selectedTrialId, setSelectedTrialId] = useState("");
  const [eligibilityTier, setEligibilityTier] = useState("");
  const [eligibilityStatus, setEligibilityStatus] = useState("");
  const [patientStatus, setPatientStatus] = useState("");
  const [formError, setFormError] = useState("");
  const [formKey, setFormKey] = useState(0);

  const linkedIds = useMemo(
    () => new Set([sourceTrialId, ...(context?.linkedTrialIds ?? [])]),
    [context?.linkedTrialIds, sourceTrialId],
  );

  const selectableTrials = useMemo(
    () => trials.filter((trial) => !linkedIds.has(trial.value)),
    [linkedIds, trials],
  );

  const filteredTrials = useMemo(
    () => filterTrials(selectableTrials, searchText),
    [searchText, selectableTrials],
  );

  const selectedTrial = useMemo(
    () => trials.find((trial) => trial.value === selectedTrialId) ?? null,
    [selectedTrialId, trials],
  );

  const tierSelectOptions = useMemo(
    () =>
      eligibilityTierOptions.map((option) => ({
        value: option.value,
        label: option.label,
      })),
    [eligibilityTierOptions],
  );

  const eligibilityStatusSelectOptions = useMemo(
    () =>
      eligibilityStatusOptions.map((option) => ({
        value: option.value,
        label: option.label,
      })),
    [eligibilityStatusOptions],
  );

  const patientStatusSelectOptions = useMemo(
    () =>
      patientStatusOptions.map((option) => ({
        value: option.value,
        label: option.label,
      })),
    [patientStatusOptions],
  );

  const stepOneValid = Boolean(selectedTrialId);
  const stepTwoValid = Boolean(eligibilityStatus && patientStatus);

  useEffect(() => {
    if (!isOpen) return;
    setStep("pick-trial");
    setSearchText("");
    setSelectedTrialId("");
    setEligibilityTier("");
    setEligibilityStatus("");
    setPatientStatus("");
    setFormError("");
    setFormKey((current) => current + 1);
  }, [context?.mrn, isOpen]);

  useEffect(() => {
    if (isOpen && !isModalOpenRef.current) {
      openModal(DIALOG_ID);
      isModalOpenRef.current = true;
    } else if (!isOpen && isModalOpenRef.current) {
      closeModal(DIALOG_ID);
      isModalOpenRef.current = false;
    }
  }, [isOpen]);

  useEffect(
    () => () => {
      if (isModalOpenRef.current) {
        closeModal(DIALOG_ID);
        isModalOpenRef.current = false;
      }
    },
    [],
  );

  /**
   * Close the oasis modal and notify the parent.
   */
  const handleClose = () => {
    closeModal(DIALOG_ID);
    isModalOpenRef.current = false;
    onClose();
  };

  /**
   * Advance to patient details or persist the monitoring link.
   */
  const handlePrimaryAction = async () => {
    if (!context || !selectedTrial) return;
    if (step === "pick-trial") {
      if (!stepOneValid) {
        setFormError("Select a clinical trial to continue");
        return;
      }
      setFormError("");
      setStep("patient-details");
      return;
    }
    if (!stepTwoValid) {
      setFormError("Complete all patient detail fields");
      return;
    }
    setFormError("");
    try {
      await onSave({
        targetTrialId: selectedTrial.value,
        eligibilityStatus,
        patientStatus,
      });
      handleClose();
    } catch (reason) {
      setFormError(
        reason instanceof Error
          ? reason.message
          : "Could not link the clinical trial",
      );
    }
  };

  if (!isOpen || !context) {
    return null;
  }

  const modalTitle =
    step === "pick-trial" ? "Add Clinical Trial" : "Add Patient Details";
  const primaryLabel =
    step === "pick-trial"
      ? isSaving
        ? "Loading…"
        : "Next"
      : isSaving
        ? "Saving…"
        : "Save";

  return (
    <Modal
      dialogId={DIALOG_ID}
      title={modalTitle}
      onSave={() => void handlePrimaryAction()}
      onCancel={handleClose}
      onClose={handleClose}
      saveButtonText={primaryLabel}
      cancelText="Cancel"
      disableSave={
        isSaving || (step === "pick-trial" ? !stepOneValid : !stepTwoValid)
      }
    >
      <div key={formKey} className="flex flex-col gap-4">
        {step === "pick-trial" ? (
          <>
            <label className="relative block">
              <span className="sr-only">Search clinical trials</span>
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primaryGray-6"
                aria-hidden
              />
              <input
                type="search"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Search by NCT ID or trial name..."
                className="w-full rounded-md border border-primaryGray-14 bg-white py-2 pl-9 pr-3 text-sm text-primaryGray-1 outline-none focus:ring-2 focus:ring-tertiaryBlue-4"
              />
            </label>
            <div className="flex max-h-72 flex-col gap-2 overflow-y-auto">
              {filteredTrials.length === 0 ? (
                <p className="text-sm text-primaryGray-4">
                  No matching trials are available for this patient.
                </p>
              ) : (
                filteredTrials.map((trial) => {
                  const isSelected = trial.value === selectedTrialId;
                  return (
                    <button
                      key={trial.value}
                      type="button"
                      className={`text-left transition-colors ${
                        isSelected
                          ? "ring-2 ring-tertiaryBlue-4 ring-offset-2 rounded-lg"
                          : ""
                      }`}
                      onClick={() => setSelectedTrialId(trial.value)}
                    >
                      <TrialSummaryCard trial={trial} />
                    </button>
                  );
                })
              )}
            </div>
          </>
        ) : (
          <>
            {selectedTrial ? <TrialSummaryCard trial={selectedTrial} /> : null}
            <Select
              id="monitoring-eligibility-tier"
              label="Eligibility Tier"
              placeholder="Select Tier"
              options={tierSelectOptions}
              value={eligibilityTier}
              defaultValue={eligibilityTier}
              onOptionChange={(option) => setEligibilityTier(option.value)}
            />
            <Select
              id="monitoring-eligibility-status"
              label="Eligibility Status"
              placeholder="Select Status"
              options={eligibilityStatusSelectOptions}
              value={eligibilityStatus}
              defaultValue={eligibilityStatus}
              onOptionChange={(option) => setEligibilityStatus(option.value)}
            />
            <Select
              id="monitoring-patient-status"
              label="Patient Status"
              placeholder="Select Patient Status"
              options={patientStatusSelectOptions}
              value={patientStatus}
              defaultValue={patientStatus}
              onOptionChange={(option) => setPatientStatus(option.value)}
            />
          </>
        )}
        {formError ? (
          <p className="text-sm text-red-600" role="alert">{formError}</p>
        ) : null}
      </div>
    </Modal>
  );
}

export default AddMonitoringTrialModal;
